import React, { useState, useMemo, useEffect } from "react";
// MUI
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import SortIcon from '@mui/icons-material/SwapVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import BudgetItemForm from '../../../components/BudgetItemForm';
import { COLORS } from '../../../lib/appColors';

const FREQ_LABELS: Record<string, string> = { WEEK: 'WEEKLY', FORTNIGHT: 'FORTNIGHTLY', MONTH: 'MONTHLY', QUARTER: 'QUARTERLY', YEAR: 'YEARLY' };

interface BucketItem {
  itemId: string;
  itemType: string;
  itemSubType: string;
  description: string;
  amount: string | number;
  frequency: string;
}

interface ItemTileGridProps {
  items: BucketItem[];
  onItemClick?: (item: BucketItem) => void;
  accentColor?: string;
}

function ItemTileGrid({ items, onItemClick, accentColor = '#3fa4f6' }: ItemTileGridProps) {
  return (
    <Grid container spacing={2} className="budget-item-grid">
      {items.map((item) => {
        const { itemId, itemSubType, description, amount, frequency } = item;
        const freqLabel = FREQ_LABELS[frequency] || frequency;
        return (
          <Grid size={{ md: 4, xs: 6 }} key={itemId} className="budget-item-cell">
            <Paper
              onClick={() => onItemClick?.(item)}
              className="budget-item-tile layout-v2"
              sx={{
                display: 'block',
                p: 1.25,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: `3px solid ${accentColor}`,
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'box-shadow .15s ease, border-color .15s ease',
                '&:hover': { borderColor: accentColor, boxShadow: `0 2px 8px 0 ${accentColor}44` },
              }}
            >
              <div className="bi-head text-center" style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.6px', fontWeight: 600, color: accentColor, marginBottom: 2 }}>
                {(itemSubType || '').trim().split('\n')[0]}
              </div>
              {description && (
                <div className="bi-desc text-center text-muted" title={description}>{description}</div>
              )}
              <div className="bi-amount big-center">${amount}</div>
              <div className={`bi-frequency bottom-center freq-${freqLabel.toLowerCase()}`}>{freqLabel}</div>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
}

type SortDirection = 'DESC' | 'ASC';
type FreqKey = 'YEARLY' | 'QUARTERLY' | 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY';

const ORDER_YEAR_TO_WEEK: FreqKey[] = ["YEARLY","QUARTERLY","MONTHLY","FORTNIGHTLY","WEEKLY"]; // descending period length
const ORDER_WEEK_TO_YEAR: FreqKey[] = [...ORDER_YEAR_TO_WEEK].reverse() as FreqKey[];

interface ItemListGroupsProps {
  accentColor?: string;
  itemType: string;
  isLoading?: boolean;
  yearBudgetItems?: BucketItem[];
  quarterBudgetItems?: BucketItem[];
  monthBudgetItems?: BucketItem[];
  fortnightBudgetItems?: BucketItem[];
  weekBudgetItems?: BucketItem[];
}

export default function ItemListGroups({ accentColor = '#3fa4f6', itemType, isLoading = false, ...props }: ItemListGroupsProps) {
  const [editingItem, setEditingItem] = useState<BucketItem | null>(null);
  const closeEdit = () => setEditingItem(null);
    // Initialize from localStorage (fallback to defaults)
    const [direction, setDirection] = useState<SortDirection>(() => {
        try { const stored = localStorage.getItem("mb_freq_sort"); return (["DESC","ASC"] as string[]).includes(stored||"") ? stored as SortDirection : "DESC"; } catch { return "DESC"; }
    }); // DESC = yearly -> weekly
    const [activeFreqs, setActiveFreqs] = useState<FreqKey[]>(() => {
        try {
            const raw = JSON.parse(localStorage.getItem("mb_freq_filters")||"null");
            const allowed: FreqKey[] = ["YEARLY","QUARTERLY","MONTHLY","FORTNIGHTLY","WEEKLY"];
            if (Array.isArray(raw)) {
                const cleaned = raw.filter((v: string) => (allowed as string[]).includes(v)) as FreqKey[];
                return cleaned.length ? cleaned : allowed;
            }
            return allowed;
        } catch { return ["YEARLY","QUARTERLY","MONTHLY","FORTNIGHTLY","WEEKLY"]; }
    });
    const toggleDirection = () => setDirection(d => d === "DESC" ? "ASC" : "DESC");
    const toggleFreq = (key: FreqKey) => setActiveFreqs(f => {
        const next = f.includes(key) ? f.filter(x=>x!==key) : [...f, key];
        return next.length ? next : [];
    });
    const clearFreqs = () => setActiveFreqs([]);
    const allFreqs = () => setActiveFreqs(["YEARLY","QUARTERLY","MONTHLY","FORTNIGHTLY","WEEKLY"]);

    const buckets = useMemo(() => ([
        { key: "YEARLY" as FreqKey, items: props.yearBudgetItems || [] },
        { key: "QUARTERLY" as FreqKey, items: props.quarterBudgetItems || [] },
        { key: "MONTHLY" as FreqKey, items: props.monthBudgetItems || [] },
        { key: "FORTNIGHTLY" as FreqKey, items: props.fortnightBudgetItems || [] },
        { key: "WEEKLY" as FreqKey, items: props.weekBudgetItems || [] }
    ]), [props.yearBudgetItems, props.quarterBudgetItems, props.monthBudgetItems, props.fortnightBudgetItems, props.weekBudgetItems]);

    const activeOrder = direction === "DESC" ? ORDER_YEAR_TO_WEEK : ORDER_WEEK_TO_YEAR;
    const orderedBuckets = activeOrder
        .filter(k => activeFreqs.includes(k))
        .map(k => buckets.find(b => b.key===k))
        .filter((b): b is { key: FreqKey; items: BucketItem[] } => !!b && b.items.length>0);
    const totalCount = orderedBuckets.reduce((acc,b)=>acc + b.items.length,0);

    // Persist
    useEffect(()=>{ try { localStorage.setItem("mb_freq_sort", direction);} catch{} }, [direction]);
    useEffect(()=>{ try { localStorage.setItem("mb_freq_filters", JSON.stringify(activeFreqs));} catch{} }, [activeFreqs]);

    // Filter menu state
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    return (
      <>
        <Paper variant="outlined" sx={{p:2, bgcolor:'background.paper', borderColor:'divider'}} className="item-groups">
            <Stack direction="row" flexWrap="wrap" alignItems="center" justifyContent="space-between" spacing={2} mb={1}>
                <Typography variant="h6" sx={{fontWeight:600}}>Items <Typography component="span" sx={{fontSize:'0.7rem', color:'text.secondary'}}>({totalCount})</Typography></Typography>
                <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={toggleDirection} aria-label={`Sort order: ${direction === "DESC" ? "Yearly to Weekly" : "Weekly to Yearly"}. Click to reverse.`} startIcon={<SortIcon fontSize="small"/>} sx={{borderColor:'divider', color:'text.secondary', '&:hover':{borderColor:COLORS.income, color:COLORS.income}}}>
                        {direction === "DESC" ? "Yearly \u2192 Weekly" : "Weekly \u2192 Yearly"}
                    </Button>
                    <IconButton aria-label="Filter by frequency" size="small" onClick={handleOpen} color={open? 'primary':'default'}>
                        <FilterListIcon fontSize="small" />
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={open} onClose={handleClose} MenuListProps={{dense:true}}>
                        {ORDER_YEAR_TO_WEEK.map(freq => {
                            const active = activeFreqs.includes(freq);
                            const label = freq.charAt(0)+freq.slice(1).toLowerCase();
                            return (
                                <MenuItem key={freq} role="menuitemcheckbox" aria-checked={active} selected={active} onClick={() => {toggleFreq(freq);}} sx={{fontSize:13}}>
                                    <input type="checkbox" readOnly checked={active} aria-hidden="true" tabIndex={-1} style={{marginRight:6}}/> {label}
                                </MenuItem>
                            );
                        })}
                        <Divider />
                        <MenuItem onClick={allFreqs} sx={{fontSize:13}}>All</MenuItem>
                        <MenuItem onClick={clearFreqs} sx={{fontSize:13}}>None</MenuItem>
                    </Menu>
                </Stack>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1} mb={2} className="freq-chips">
                {activeFreqs.length === 0 && (
                    <Typography variant="caption" color="text.secondary">No frequencies selected.</Typography>
                )}
                {activeFreqs.length > 0 && activeOrder.filter(f=>activeFreqs.includes(f)).map(f => {
                    const label = f.charAt(0)+f.slice(1).toLowerCase();
                    return <Chip key={f} label={label} size="small" aria-label={`Filter: ${label}. Click to remove.`} onDelete={()=>toggleFreq(f)} sx={{border:'1px solid', borderColor:'divider', bgcolor:'transparent'}}/>;
                })}
                {activeFreqs.length>0 && activeFreqs.length<ORDER_YEAR_TO_WEEK.length && (
                    <Chip label="Reset" size="small" onClick={allFreqs} aria-label="Reset frequency filters to show all" variant="outlined" />
                )}
            </Stack>
            <div className="text-center">
                {isLoading ? (
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px,1fr))', gap:'1rem', padding:'0.5rem 0'}}>
                        {Array.from({length: 6}).map((_, i) => (
                            <div key={i} style={{height:120, borderRadius:8, background:'linear-gradient(90deg,rgba(0,0,0,0.03) 25%,rgba(0,0,0,0.06) 37%,rgba(0,0,0,0.03) 63%)', backgroundSize:'400% 100%', animation:'sg-pulse 1.2s linear infinite', border:`1px solid ${accentColor}22`, borderLeft:`3px solid ${accentColor}44`}} />
                        ))}
                    </div>
                ) : (
                    <>
                        {orderedBuckets.map((bucket, idx) => (
                            <React.Fragment key={bucket.key}>
                                <ItemTileGrid items={bucket.items} onItemClick={setEditingItem} accentColor={accentColor} />
                                {idx < orderedBuckets.length - 1 && <hr className="my-4" />}
                            </React.Fragment>
                        ))}
                        {orderedBuckets.length === 0 && <Typography variant="caption" color="text.secondary" sx={{pt:3, display:'block'}}>No items yet.</Typography>}
                    </>
                )}
            </div>
        </Paper>

        <Dialog open={!!editingItem} onClose={closeEdit} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                Edit Item
                <IconButton size="small" onClick={closeEdit} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                {editingItem && (
                    <BudgetItemForm
                        mode="edit"
                        initial={editingItem}
                        fixedType={editingItem.itemType || itemType}
                        onSuccess={closeEdit}
                        enableDelete
                    />
                )}
            </DialogContent>
        </Dialog>
      </>
    );
}
